import { Component, computed, inject, Injector, Input, OnInit, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BaseModalComponent } from '@card-software/library';
import { AuthService, Site, SiteService } from '@card-software/nexus-utilities';
import { FormControl } from '@angular/forms';
import { forkJoin, Observable, of } from 'rxjs';
//* Models
import { Group } from '@app/models/user-manager/group';
import { Role } from '@app/models/user-manager/roles';
import { User, SiteProperties } from '@app/models/user-manager/user';
import { Enum } from '@app/models/enums/enums';
import { UserManagerAdminGroup } from '@app/models/authorization/enum/user-group.enum';
import { isDelegatedUserManager } from '@app/utilities/delegate-role.util';

//* Services
import { EnumsProxyService } from '@app/proxies/enums.proxy.service';
import { GroupProxyService } from '@app/proxies/group.proxy.service';
import { MessageService } from 'primeng/api';
import { RolesProxyService } from '@app/proxies/roles.proxy.service';
import { SiteProxyService } from '@app/proxies/site.proxy.service';
import { UserProxyService } from '@app/proxies/user.proxy.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { ToastService } from '@app/services/toast.service';
import { ResponseTranslation } from '@app/models/translation';

interface options {
  id?: string;
  username?: string;
}

/** Error body of the UserMaster API (ApiResponse with a bilingual message). */
interface ApiErrorBody {
  errorMessage?: ResponseTranslation | null;
}

/** Close action when the API answers 404 for the user: the list refreshes and the toast was shown here. */
export const USER_NOT_AVAILABLE = 'userNotAvailable';

interface SitePanel {
  siteId: string;
  selectedGroups: Group[];
  availableGroups: Group[];
}

@Component({
  selector: 'app-user-modal',
  templateUrl: './user-modal.component.html',
  styleUrl: './user-modal.component.scss',
})
export class UserModalComponent extends BaseModalComponent<options | null> implements OnInit {
  @Input() user: User | null = null;
  editPassword = signal(true);
  private passwordUi = signal('');
  blockButton = computed(() => this.editPassword() && this.passwordUi().length <= 0);
  allGroups: Group[] = [];
  selectedGroups: Group[] = [];
  listRoles = signal<Role[]>([]);
  listStatus = signal<{ label: string; value: number }[]>([]);

  firstName = new FormControl('');
  middleName = new FormControl('');
  lastName = new FormControl('');
  secondLastName = new FormControl('');
  email = new FormControl({ value: '', disabled: false });
  userName = new FormControl({ value: '', disabled: false });
  password = new FormControl('');
  avatar = new FormControl('');
  status = new FormControl(1);
  roles = new FormControl([] as string[]);
  /** Managed by external user admins (#6295): internal admins choose; a delegate's users always are (read-only). */
  externallyManaged = new FormControl(false, { nonNullable: true });

  // Multi-site (only when canAssignSites)
  selectedSiteIds: string[] = [];
  // Full site catalog (loaded for assigners) so names resolve even for sites the
  // editor doesn't belong to; falls back to the editor's own sites.
  allSites: Site[] = [];
  sitePanels: SitePanel[] = [];
  expandedSiteIds: string[] = [];
  submittedForm = false;

  isUpdate = signal(false);

  /**
   * Delegated user manager (#6263): no roles, no site selection, only the delegable groups the API returns.
   * The API enforces all of it (#6261); this only hides what they can't use.
   */
  readonly isDelegate = computed(() => isDelegatedUserManager());
  /** A delegate's user also holds groups the delegate can't see (#6300): the API keeps them; the form says so. */
  readonly hasHiddenGroups = signal(false);

  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  constructor(
    injector: Injector,
    private userProxyService: UserProxyService,
    private messageService: MessageService,
    private rolesProxyService: RolesProxyService,
    private siteProxyService: SiteProxyService,
    private groupProxyService: GroupProxyService,
    private enumsProxyService: EnumsProxyService,
    private localHelper: LocaleHelperService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.password.valueChanges.subscribe((value) => {
      this.passwordUi.set(value ?? '');
    });

    if (this.isDelegate()) {
      this.externallyManaged.setValue(true);
      this.externallyManaged.disable();
    }

    this.getOptions();
  }

  get canAssignSites(): boolean {
    // A delegate manages the active site only, even if they also hold UserSiteAssigner.
    return !this.isDelegate() && AuthService.hasRole(UserManagerAdminGroup.UserSiteAssigner);
  }

  /** Name of the active site, shown read-only to a delegate. */
  get activeSiteName(): string {
    return this.getSiteName(SiteService.getSelectedSiteId() ?? '');
  }

  get availableSites(): Site[] {
    // Assigners get the full catalog (loaded into allSites); otherwise the editor's own sites.
    return this.allSites.length > 0 ? this.allSites : SiteService.getSites();
  }

  getSiteName(siteId: string): string {
    return this.availableSites.find((s) => s.id === siteId)?.name ?? siteId;
  }

  onSitesChange(siteIds: string[]) {
    this.selectedSiteIds = siteIds ?? [];
    this.syncSitePanels();
  }

  private syncSitePanels() {
    const previous = new Map(this.sitePanels.map((p) => [p.siteId, p]));
    const next: SitePanel[] = this.selectedSiteIds.map((siteId) => {
      const existing = previous.get(siteId);
      if (existing) return existing;
      // Build initial state from user.siteProperties or empty
      const sp = this.user?.siteProperties?.find((s) => s.siteId === siteId);
      const groupIds = sp?.groupIds ?? [];
      return {
        siteId,
        selectedGroups: this.allGroups.filter((g) => groupIds.includes(g.id!)),
        availableGroups: this.allGroups.filter((g) => !groupIds.includes(g.id!)),
      };
    });
    this.sitePanels = next;
    this.expandedSiteIds = next.map((p) => p.siteId);
  }

  private setInitialData(user: User) {
    if (!user) return;
    this.editPassword.set(false);
    this.disabledFields();
    this.userName.setValue(user.username ?? '');
    this.email.setValue(user.email ?? '');

    this.firstName.setValue(user.profile!.firstName ?? '');
    this.lastName.setValue(user.profile!.lastName ?? '');
    this.middleName.setValue(user.profile!.middleName ?? '');
    this.secondLastName.setValue(user.profile!.secondLastName ?? '');
    this.status.setValue(user.status ?? 0);
    this.roles.setValue(user.roleIds ?? []);
    if (!this.isDelegate()) this.externallyManaged.setValue(user.isExternallyManaged ?? false);

    if (this.canAssignSites) {
      // Pre-fill selected sites and per-site groups from user.siteProperties
      this.selectedSiteIds = user.siteIds ?? [];
      this.syncSitePanels();
    } else {
      // Single-site: read groups from SiteProperties of active site
      const activeSiteId = SiteService.getSelectedSiteId() ?? '';
      const currentSp = user.siteProperties?.find((sp) => sp.siteId === activeSiteId) ?? user.siteProperties?.[0];
      const userGroupIds = currentSp?.groupIds ?? [];
      // A delegate only gets the visible groups from group/all; any other id on the user is a hidden group.
      const visibleGroupIds = new Set(this.allGroups.map((g) => g.id));
      this.hasHiddenGroups.set(this.isDelegate() && userGroupIds.some((id) => !visibleGroupIds.has(id)));
      this.selectedGroups = this.allGroups.filter((g) => userGroupIds.includes(g.id!));
      this.allGroups = this.allGroups.filter((g) => !userGroupIds.includes(g.id!));
    }
  }

  private getOptions() {
    forkJoin([
      // Delegates never see roles (the API answers 403).
      this.isDelegate() ? of(null) : this.rolesProxyService.getAllRoles(),
      this.groupProxyService.getAllGroups(),
      this.enumsProxyService.getEnums(),
      this.canAssignSites ? this.siteProxyService.getAllSites() : of(null),
    ]).subscribe(([roles, groups, enums, sites]) => {
      if (roles && roles.success && roles.data) {
        this.listRoles.set(roles.data);
      }
      if (groups.success && groups.data) {
        this.allGroups = groups.data;
      }
      if (sites && sites.success && sites.data) {
        this.allSites = sites.data;
      }
      this.setOptionsForPrimeNg(enums);

      // Set initial data after groups are loaded
      if (this.user) {
        this.isUpdate.set(true);
        this.setInitialData(this.user);
      } else if (this.canAssignSites) {
        // New user with role: pre-select active site
        const activeSiteId = SiteService.getSelectedSiteId();
        if (activeSiteId) {
          this.selectedSiteIds = [activeSiteId];
          this.syncSitePanels();
        }
      }
    });
  }

  private setOptionsForPrimeNg(value: Enum[]) {
    const enumUser = value.find((e) => e.name === 'UserStatus');
    this.listStatus.set(
      Object.entries(enumUser!.values).map(([key, label]) => ({
        label: this.localHelper.getLocalizedValue(label),
        value: Number(key),
      })),
    );
  }

  private disabledFields() {
    this.email.disable();
    this.userName.disable();
  }

  generateAutomaticPassword() {
    const string = Math.random().toString(36).substring(2, 15);
    this.password.setValue(string);
  }

  onPanelGroupsChanged(panel: SitePanel) {
    panel.availableGroups = [...panel.availableGroups];
    panel.selectedGroups = [...panel.selectedGroups];
  }

  onSingleSiteGroupsChanged() {
    this.allGroups = [...this.allGroups];
    this.selectedGroups = [...this.selectedGroups];
  }

  onClose() {
    this.close({ action: 'close', data: null });
  }

  private buildSiteProperties(): { siteIds: string[] | undefined; siteProperties: SiteProperties[] } {
    // A delegate never sends roles: the API refuses them (#6261).
    const selectedRoleIds = this.isDelegate() ? [] : (this.roles.value ?? []);

    if (this.canAssignSites) {
      const siteProperties: SiteProperties[] = this.sitePanels.map((p) => ({
        siteId: p.siteId,
        roleIds: selectedRoleIds,
        groupIds: p.selectedGroups.map((g) => g.id!),
      }));
      return { siteIds: this.selectedSiteIds, siteProperties };
    }

    // Without role: build only the active site entry, preserve other sites' entries
    const activeSiteId = SiteService.getSelectedSiteId() ?? '';
    const currentEntry: SiteProperties = {
      siteId: activeSiteId,
      roleIds: selectedRoleIds,
      groupIds: this.selectedGroups.map((g) => g.id!),
    };
    // A delegate only edits the active site; the API keeps the other sites and the groups they can't see (#6300).
    const otherSites = this.isDelegate() ? [] : (this.user?.siteProperties ?? []).filter((sp) => sp.siteId !== activeSiteId);
    return { siteIds: undefined, siteProperties: [...otherSites, currentEntry] };
  }

  onSubmit() {
    this.submittedForm = true;

    if (this.canAssignSites && this.selectedSiteIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'At least one site must be selected.',
        key: 'main',
        life: 2500,
      });
      return;
    }

    const { siteIds, siteProperties } = this.buildSiteProperties();

    const user: User = {
      id: this.user?.id ?? '',
      email: this.email.value ?? '',
      username: this.userName.value ?? '',
      password: this.password.value ?? '',
      profile: {
        firstName: this.firstName.value ?? '',
        middleName: this.middleName.value ?? '',
        lastName: this.lastName.value ?? '',
        secondLastName: this.secondLastName.value ?? '',
        avatarUrl: '',
      },
      status: this.status.value ?? 0,
      siteIds,
      siteProperties,
      // The API forces true for a delegate's new user either way (#6295).
      isExternallyManaged: this.externallyManaged.value,
    };

    if (this.isUpdate()) {
      // A delegate gets "User not available" instead of the generic error toast when the user is no longer theirs.
      const ownErrors = this.isDelegate();
      const groupsRolesUpdates = siteProperties.map((sp) => this.userProxyService.updateGroupsAndRoles(user, sp.siteId, ownErrors));

      // Prune sites the assigner explicitly deselected. Base this ONLY on the sites the
      // multi-select actually showed (user.siteIds, which seeded selectedSiteIds in
      // setInitialData) — never on siteProperties. Otherwise a legacy user whose SiteIds is
      // incomplete relative to SiteProperties would have those unshown sites silently removed.
      const shownSiteIds = this.user?.siteIds ?? [];
      const removedSiteIds = this.canAssignSites ? shownSiteIds.filter((id) => !this.selectedSiteIds.includes(id)) : [];
      const removalUpdates = removedSiteIds.map((siteId) => this.userProxyService.removeSite(user.id!, siteId));

      const trendForUpdating: Observable<unknown>[] = [
        this.userProxyService.updateProfile(user, ownErrors),
        ...groupsRolesUpdates,
        ...removalUpdates,
        this.userProxyService.updateStatus(user, ownErrors),
      ];

      if (this.editPassword()) {
        trendForUpdating.push(this.userProxyService.updatePassword(user, ownErrors));
      }

      // Only internal admins change it, and only when it changed (the API audits every change).
      if (!this.isDelegate() && user.isExternallyManaged !== (this.user?.isExternallyManaged ?? false)) {
        trendForUpdating.push(this.userProxyService.updateExternallyManaged(user));
      }

      forkJoin(trendForUpdating.length > 0 ? trendForUpdating : [of(null)]).subscribe({
        next: () => {
          this.onClose();
          this.messageService.add({
            severity: 'secondary',
            summary: 'Success',
            detail: `User ${user.username}  updated successfully`,
            key: 'main',
            life: 2500,
          });
        },
        error: (error: unknown) => {
          if (ownErrors) this.onSaveError(error);
        },
      });
      return;
    }

    this.userProxyService.createUser(user).subscribe({
      next: (response) => {
        this.onClose();
        this.messageService.add({
          severity: 'secondary',
          summary: 'Success',
          detail: `User '${response.data?.username}' created successfully`,
          key: 'main',
          life: 2500,
        });
      },
    });
  }

  /**
   * A delegate's save failed. 404: the user is no longer one they manage (or no longer exists), so close the
   * details, say so, and let the list refresh. Anything else: show the API's message.
   */
  private onSaveError(error: unknown): void {
    if (error instanceof HttpErrorResponse && error.status === 404) {
      this.toast.warn(this.translate.instant('Users_NotAvailable_Title'), this.translate.instant('Users_NotAvailable_Detail'), 6000);
      this.close({ action: USER_NOT_AVAILABLE, data: null });
      return;
    }

    const body: ApiErrorBody | null = error instanceof HttpErrorResponse ? (error.error as ApiErrorBody | null) : null;
    const message = this.localHelper.getLocalizedValue(body?.errorMessage) || this.translate.instant('Users_Save_Error');
    this.toast.error(this.translate.instant('Common_Error'), message);
  }
}
